/* eslint-disable no-useless-escape */
import React, { useState, useEffect} from "react";
import OTPInput from "../../../Components/Forms/OTP/OTPForm";
import { useFormik } from "formik";
import * as Yup from "yup";
import { sendCodeTimer } from "../../../Methods/Helpers/MiscHelper";
import Loader from "../../../Components/Helpers/Loaders/Loader";
import { initiateResetPassword } from "../../../Store/Provider/ProviderAPI";
import { toastError, toastSuccess } from "../../../Methods/Helpers/ToastHelper";
import SVG from "react-inlinesvg";

type ResetPassword = {
    verificationCode: string;
    password: string;
    confirmPassword: string;
}

type ResetPassFocusStateType = {
    password: boolean;
    confirmPassword: boolean;
}

type ResetPasswordFormTypeDef = {
  onSubmitForm: (values: any) => void;
  onCloseCancel: () => void;
  isInitialSendCode: boolean;
  username: string;
}

const initialValues: ResetPassword = {
    verificationCode: "",
    password: "",
    confirmPassword: "",
}
const passwordSchema = Yup.object().shape({
    verificationCode: Yup.string().min(6, "Verification code should be 6 character long").required("Verification code is required"),
    password: Yup.string().required("Password is required").min(8, 'Password is too short - should be 8 chars minimum.')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])/, "Must contain one uppercase, one lowercase, one number and one special case character"),
    confirmPassword: Yup.string().required("Confirm password is required").oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

//form-control Error class=> form-control-lg is-invalid
const ForgotPasswordForm: React.FC<ResetPasswordFormTypeDef> = (props) => {
    const {onSubmitForm, onCloseCancel, isInitialSendCode = false, username} = props;
    const [isSendCode, setIsSendCode] = useState<boolean>(false);
    const maxWaitTime: number = 60;
    const [seconds, setSeconds] = useState<number>(maxWaitTime);
    const [code, setCode] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState<ResetPassFocusStateType>({password: false, confirmPassword: false});

    const formik = useFormik({
        initialValues: {...initialValues},
        validationSchema: passwordSchema,
        validateOnChange: true,
        validateOnBlur: true,
        validateOnMount: true,
        onSubmit: (values)=>{
            onSubmitForm(values);
        }
    });

    useEffect(() => {
        sendCodeTimer(seconds, setSeconds, setIsSendCode, maxWaitTime);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitialSendCode]);
  
    const onChange = (otps: string) => {
        setCode(otps.trim());
        formik.setTouched({...formik.touched, verificationCode: true});
        formik.setValues({...formik.values, verificationCode: otps.trim()}, true);
    }

    const resendCode = async () => {
        setIsSendCode(true);
        setLoading(true);
        await initiateResetPassword(username).then((response: any) => {
        if(response.success){
            toastSuccess(response.message);
        }else{
            if(response.code === "LimitExceededException"){
            toastError("Attempt limit exceeded, please try after some time.");
            }else{
            toastError(response.message);
            }
        }
        setLoading(false);
        }).catch((error: any) => {
        toastError(error.message);
        setLoading(false);
        });
        setCode("");
        sendCodeTimer(seconds, setSeconds, setIsSendCode)
    }


    const handleBlur = (e: any, focusFor: string) => {
        setIsFocused({...isFocused, [focusFor]: false});
        formik.handleBlur(e);
    };

    return (
        <>
            <div className="card-inner create-account">
                <div className={`user-detail active verify-otp`}>
                    <form 
                    onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                    return false;
                    }}
                    noValidate
                    autoComplete="off">
                        <div className="lable-main mb-2" style={{color: "#66CFC8"}}>Verification Code</div>
                        <div className="mb-4">
                        <div className="d-flex align-items-center">
                            <OTPInput 
                            otpvalue={code} 
                            otplength={6} 
                            styleclasses={"form-control"} 
                            onChange={onChange}
                            />
                            <div className="send-btn">
                                <button
                                type="button"
                                className="btn btn-success fs-12 text-nowrap py-2 btn-small-shadow"
                                onClick={resendCode}
                                disabled={isSendCode}
                                style={{minWidth:"70px"}}>
                                    {!isSendCode ? "Resend" : (seconds < 10 ? "0"+ seconds : seconds) + "s"}
                                </button>
                            </div>
                        </div>
                        </div>
                        <div className="floating-input-container mt-4">
                            <div className={`password-inner-field ${isFocused.password || formik.values.password ? 'focused' : ''}`} style={{ ...{ borderColor: isFocused.password ? "#66CFC8" : "#C4C4C4" } }}>
                            <input
                            placeholder=" "
                            name="password"
                            className="form-control"
                            type="password"
                            onFocus={()=>setIsFocused({...isFocused, password: true})}
                            value={formik.values.password}
                            autoComplete="off"
                            onChange={formik.handleChange}
                            onKeyUp={formik.handleBlur}
                            onBlur={(e: any) => handleBlur(e, "password")}
                            />
                            </div>
                            <label className="float-label" htmlFor="">New password</label>
                            {formik.touched.password && !formik.errors.password ?
                                (
                                    <span className="tick-validate right-position">
                                        <SVG
                                            className="tick-icon"
                                            src="Assets/Images/Icons/tick-icon.svg"
                                            width="16"
                                            height="16"
                                        />
                                    </span>
                                ) : (
                                    <div className="info-content">
                                        <span className="info-icon position-right show-info-on-hover">
                                            <SVG
                                                className="tooltip-icon"
                                                src="Assets/Images/Icons/Tooltip.svg"
                                                width="26"
                                                height="26"
                                            />
                                        </span>
                                        <div className="password-info-content card">
                                            <b className="theme-text">Password Policy:</b>
                                            <ul className="ps-3">
                                                <li>Minimum length of 8 characters</li>
                                            </ul>
                                            <b className="theme-text">
                                                Contain at least 1 of the following:
                                            </b>
                                            <ul className="ps-3 mb-0">
                                                <li>One uppercase character</li>
                                                <li>One lowercase character </li>
                                                <li>One special character</li>
                                                <li>One number</li>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            {formik.touched.password && formik.errors.password ? <label className="text-error">{formik.errors.password}</label> : ""}
                        </div>
                        <div className="floating-input-container mt-4">
                            <div className={`password-inner-field ${isFocused.confirmPassword || formik.values.confirmPassword ? 'focused' : ''}`} style={{ ...{ borderColor: isFocused.confirmPassword ? "#66CFC8" : "#C4C4C4" } }}>
                                <input
                                placeholder=" " 
                                name="confirmPassword"
                                className="form-control"
                                type="password"
                                value={formik.values.confirmPassword}
                                autoComplete="off"
                                onChange={formik.handleChange}
                                onKeyUp={formik.handleBlur}
                                onBlur={(e: any) => handleBlur(e, "confirmPassword")}
                                onFocus={() => setIsFocused({...isFocused, confirmPassword: true})}
                                />
                            </div>
                            <label className="float-label" htmlFor="">Confirm password</label>
                            {formik.touched.confirmPassword && !formik.errors.confirmPassword &&
                                <span className="tick-validate right-position">
                                    <SVG
                                        className="tick-icon"
                                        src="Assets/Images/Icons/tick-icon.svg"
                                        width="16"
                                        height="16"
                                    />
                                </span>
                            }
                            {formik.touched.confirmPassword && formik.errors.confirmPassword ? <label className="text-error">{formik.errors.confirmPassword}</label> : ""}
                        </div>
                        <div className="btn-pair d-flex rest-password-pair-btn">
                            <button type="button" onClick={onCloseCancel} className="btn btn-success success-reverse">Cancel</button>
                            <button 
                            type="submit"
                            className={`btn ${!formik.isValid ? "btn-custom-disabled" : "btn-success"}`} disabled={!formik.isValid}>
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            {loading && <Loader />}
        </>
    );
};

export default ForgotPasswordForm;
